import { useMemo, useState } from "react";

export type SearchableItem = {
    id: string;
    searchableText: string;
    [key: string]: any;
};

export type UseSearchOptions<T extends SearchableItem> = {
    items: T[];
    searchFields?: (keyof T)[];
    caseSensitive?: boolean;
};

export function useSearch<T extends SearchableItem>({
    items,
    searchFields = ["searchableText" as keyof T],
    caseSensitive = false,
}: UseSearchOptions<T>) {
    const [searchTerm, setSearchTerm] = useState("");

    const filteredItems = useMemo(() => {
        if (!searchTerm.trim()) return items;

        const normalizedSearch = caseSensitive
            ? searchTerm.trim()
            : searchTerm.trim().toLowerCase();

        return items.filter((item) =>
            searchFields.some((field) => {
                const fieldValue = String(item[field] || "");
                const normalizedField = caseSensitive
                    ? fieldValue
                    : fieldValue.toLowerCase();

                return normalizedField.includes(normalizedSearch);
            }),
        );
    }, [items, searchTerm, searchFields, caseSensitive]);

    return {
        searchTerm,
        setSearchTerm,
        filteredItems,
        hasResults: filteredItems.length > 0,
        totalResults: filteredItems.length,
    };
}

export default useSearch;
